import { simpleSort } from './simpleSort';

/**
 * Centralized sorting utilities for consistent sorting behavior across the application
 */

// Constants for sort direction validation
export const SORT_DIRECTIONS = {
  ASC: 'asc',
  DESC: 'desc'
};

/**
 * Valid sort directions array for validation
 */
const VALID_DIRECTIONS = Object.values(SORT_DIRECTIONS);

/**
 * Generic sorting function with error handling and direction validation
 * @param {Array} array - Array to sort
 * @param {string|Function} sortBy - Property name or custom sort function
 * @param {string} direction - 'asc' or 'desc'
 * @returns {Array} - Sorted array (new array, original unchanged)
 */
export const sortArray = (array, sortBy = 'name', direction = SORT_DIRECTIONS.ASC) => {
  if (!Array.isArray(array) || array.length === 0) {
    return [];
  }

  // Validate direction parameter
  if (!VALID_DIRECTIONS.includes(direction)) {
    console.warn(`Invalid sort direction "${direction}". Using default "asc". Valid directions: ${VALID_DIRECTIONS.join(', ')}`);
    direction = SORT_DIRECTIONS.ASC;
  }

  try {
    const sortedArray = [...array].sort((a, b) => {
      if (typeof sortBy === 'function') {
        return sortBy(a, b);
      }

      const result = simpleSort(a, b, sortBy);
      return direction === SORT_DIRECTIONS.DESC ? -result : result;
    });

    return sortedArray;
  } catch (error) {
    console.warn('Sorting failed, returning original array:', error);
    return [...array];
  }
};

/**
 * Sort case states by name
 * @param {Array} caseStates - Array of case state objects
 * @param {string} direction - Sort direction ('asc' or 'desc')
 * @returns {Array} - Sorted array of case states
 */
export const sortCaseStates = (caseStates, direction = SORT_DIRECTIONS.ASC) => {
  return sortArray(caseStates, 'name', direction);
};

/**
 * Sort workflows by name
 * @param {Array} workflows - Array of workflow objects
 * @param {string} direction - Sort direction ('asc' or 'desc')
 * @returns {Array} - Sorted array of workflows
 */
export const sortWorkflows = (workflows, direction = SORT_DIRECTIONS.ASC) => {
  return sortArray(workflows, 'name', direction);
};

/**
 * Sort roles by name
 * @param {Array} roles - Array of role objects
 * @param {string} direction - Sort direction ('asc' or 'desc')
 * @returns {Array} - Sorted array of roles
 */
export const sortRoles = (roles, direction = SORT_DIRECTIONS.ASC) => {
  return sortArray(roles, 'name', direction);
};

/**
 * Sort case actions by title
 * @param {Array} caseActions - Array of case action objects
 * @param {string} direction - Sort direction ('asc' or 'desc')
 * @returns {Array} - Sorted array of case actions
 */
export const sortCaseActions = (caseActions, direction = SORT_DIRECTIONS.ASC) => {
  return sortArray(caseActions, 'title', direction);
};

/**
 * Sort admin sections by label
 * @param {Array} adminSections - Array of admin section objects
 * @param {string} direction - Sort direction ('asc' or 'desc')
 * @returns {Array} - Sorted array of admin sections
 */
export const sortAdminSections = (adminSections, direction = SORT_DIRECTIONS.ASC) => {
  return sortArray(adminSections, 'label', direction);
};

/**
 * Sort case highlight configs by title
 * @param {Array} caseHighlightConfigs - Array of case highlight config objects
 * @param {string} direction - Sort direction ('asc' or 'desc')
 * @returns {Array} - Sorted array of case highlight configs
 */
export const sortCaseHighlightConfigs = (caseHighlightConfigs, direction = SORT_DIRECTIONS.ASC) => {
  return sortArray(caseHighlightConfigs, 'title', direction);
};

/**
 * Sort skins by filename
 * @param {Array} skins - Array of skin objects
 * @param {string} direction - Sort direction ('asc' or 'desc')
 * @returns {Array} - Sorted array of skins
 */
export const sortSkins = (skins, direction = SORT_DIRECTIONS.ASC) => {
  return sortArray(skins, 'filename', direction);
};

/**
 * Sort by order property (commonly used for display order)
 * @param {Array} array - Array to sort
 * @param {string} direction - Sort direction ('asc' or 'desc')
 * @returns {Array} - Sorted array by order property
 */
export const sortByOrder = (array, direction = SORT_DIRECTIONS.ASC) => {
  return sortArray(array, (a, b) => {
    const aOrder = a.order || 0;
    const bOrder = b.order || 0;
    return aOrder - bOrder;
  }, direction);
};

/**
 * Sort by position property (commonly used for display position)
 * @param {Array} array - Array to sort
 * @param {string} direction - Sort direction ('asc' or 'desc')
 * @returns {Array} - Sorted array by position property
 */
export const sortByPosition = (array, direction = SORT_DIRECTIONS.ASC) => {
  return sortArray(array, (a, b) => {
    const aPosition = a.position || 0;
    const bPosition = b.position || 0;
    return aPosition - bPosition;
  }, direction);
};

/**
 * Sort filter values by value or name property
 * @param {Array} values - Array of filter value objects
 * @param {string} direction - Sort direction ('asc' or 'desc')
 * @returns {Array} - Sorted array of filter values
 */
export const sortFilterValues = (values, direction = SORT_DIRECTIONS.ASC) => {
  return sortArray(values, (a, b) => {
    // Filter values can have either 'name' or 'value' property
    const aVal = a.name || a.value || '';
    const bVal = b.name || b.value || '';
    return simpleSort(aVal, bVal);
  }, direction);
};

/**
 * Sort filters by title
 * @param {Array} filters - Array of filter objects
 * @param {string} direction - Sort direction ('asc' or 'desc')
 * @returns {Array} - Sorted array of filters
 */
export const sortFilters = (filters, direction = SORT_DIRECTIONS.ASC) => {
  return sortArray(filters, 'title', direction);
};

/**
 * Sort workflow attributes by attribute name
 * @param {Array} attributes - Array of workflow attribute objects
 * @param {string} direction - Sort direction ('asc' or 'desc')
 * @returns {Array} - Sorted array of workflow attributes
 */
export const sortWorkflowAttributes = (attributes, direction = SORT_DIRECTIONS.ASC) => {
  return sortArray(attributes, 'attributeName', direction);
};